import { FriendlyCaptchaSDK, WidgetHandle } from '@friendlycaptcha/sdk';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ButtonComponent, HelpTooltipComponent, InfoBannerComponent } from '@catch/general-ui';
import { LanguageService, ThemingService, ToastsService } from '@catch/services';
import { TranslocoPipe } from '@jsverse/transloco';
import { emailRegex } from '../../../../../../assets/regex';
import { environment } from '../../../../../../environments/environment';
import { PlatformAuthService } from '../../../../services/platform-auth.service';

@Component({
  selector: 'app-platform-register-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    ButtonComponent,
    HelpTooltipComponent,
    InfoBannerComponent,
  ],
  templateUrl: './platform-register-form.component.html',
  styleUrl: './platform-register-form.component.scss',
})
export class PlatformRegisterFormComponent implements AfterViewInit {
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly route = inject(ActivatedRoute);
  private readonly auth = inject(PlatformAuthService);
  private readonly toastsService = inject(ToastsService);
  private readonly languageService = inject(LanguageService);
  private readonly themingService = inject(ThemingService);
  private readonly friendlyCaptcha = new FriendlyCaptchaSDK();
  private readonly captchaHost = viewChild<ElementRef<HTMLElement>>('friendlyCaptcha');
  private captchaWidget: WidgetHandle | null = null;

  readonly prefillEmail = input<string | null>(null);

  registerForm: FormGroup = this.fb.group({
    email: [null, [Validators.required, Validators.email, Validators.pattern(emailRegex)]],
    captcha: [null, Validators.required],
  });

  registerFormSubmitAttempt: WritableSignal<boolean> = signal(false);
  registerAttempt: WritableSignal<boolean> = signal(false);
  registerSuccess: WritableSignal<boolean> = signal(false);
  termsAccepted: WritableSignal<boolean> = signal(false);
  talenthubAccepted: WritableSignal<boolean> = signal(false);

  onLoading = output<boolean>();

  loadingEffect = effect(() => {
    this.onLoading.emit(this.registerAttempt());
  });

  /**
   * Prefills the email from modal options or a jobboard apply CTA query param.
   */
  constructor() {
    effect(() => {
      const email = this.prefillEmail() ?? this.route.snapshot.queryParamMap.get('email');
      if (email && this.email.value !== email) {
        this.email.setValue(email);
      }
    });
  }

  /**
   * Mounts the FriendlyCaptcha v2 widget after the host element exists.
   */
  ngAfterViewInit(): void {
    this.initCaptchaWidget();
  }

  /**
   * Submits the email-first registration form.
   */
  onRegisterFormSubmit(): void {
    const captchaResponse = this.captchaWidget?.getResponse();
    if (captchaResponse) {
      this.captchaCode.setValue(captchaResponse);
    } else if (this.captchaCode.value) {
      this.captchaCode.setValue(null);
    }

    this.registerFormSubmitAttempt.set(true);

    if (this.registerForm.valid && this.termsAccepted()) {
      this.registerAttempt.set(true);
      this.auth
        .register({
          email: this.email.value,
          captchaCode: this.captchaCode.value ?? '',
          termsAccepted: true,
          talenthubAccepted: this.talenthubAccepted(),
        })
        .subscribe({
          next: () => {
            this.registerSuccess.set(true);
            this.registerAttempt.set(false);
            this.cdr.markForCheck();
          },
          error: () => {
            this.registerAttempt.set(false);
            this.toastsService.warning('jobboard.platform.auth.register.error');
            this.captchaCode.setValue(null);
            this.captchaWidget?.reset();
            this.cdr.markForCheck();
          },
        });
    }
  }

  /**
   * Creates the FriendlyCaptcha v2 widget used for candidate registration.
   */
  private initCaptchaWidget(): void {
    const host = this.captchaHost()?.nativeElement;
    if (!host) {
      return;
    }
    this.captchaWidget = this.friendlyCaptcha.createWidget({
      sitekey: environment.friendlyCaptchaV2SiteKey,
      element: host,
      startMode: 'auto',
      formFieldName: null,
      language: (this.languageService.currentLanguage?.value ?? 'de-DE').substring(0, 2),
      theme: this.themingService.theme === 'dark' ? 'dark' : 'light',
    });
    this.captchaWidget.addEventListener('frc:widget.complete', (event) => {
      this.captchaCode.setValue(event.detail.response);
    });
  }

  get email() {
    return this.registerForm.get('email')!;
  }

  get captchaCode() {
    return this.registerForm.get('captcha') as FormControl<string | null>;
  }
}
